// Loan record foundations — ADMIN-only edit of an LS Setup catalog item,
// mirroring Deal Flow's <CriteriaCatalogEditModal>. Per-loan checklist rows
// FK the catalog by GUID, so edits propagate to every loan. Code stays
// immutable; Tier is editable but guarded — it moves the Mark-Setup-Complete
// gate everywhere, so a change shows a warning and requires a justification.
// FIND-12: every field hydrates from the catalog row and an untouched save
// sends the values back byte-identical (tier omitted when unchanged).

import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Badge, Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useUpdateSetupCatalogItem } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import {
  LOAN_SETUP_SECTIONS,
  loanSetupTierLabel,
  type LoanSetupCatalogItemDto,
  type LoanSetupTier,
  type LoanSummaryDto,
} from "@/api/portfolioAdmin/types";
import { joinOwnerRoleCodes, splitOwnerRoleCodes } from "./setupCatalogForm";
import { DueDateFormulaField, OwnerRolesField, TierField } from "./SetupCatalogFormFields";

interface SetupCatalogEditModalProps {
  loan: LoanSummaryDto;
  open: boolean;
  onClose: () => void;
  catalog: LoanSetupCatalogItemDto;
}

const TITLE_MAX = 200;

export function SetupCatalogEditModal({
  loan,
  open,
  onClose,
  catalog,
}: SetupCatalogEditModalProps) {
  const toast = useToast();
  const update = useUpdateSetupCatalogItem(loan.id);

  const [title, setTitle] = useState(catalog.title);
  const [tier, setTier] = useState<LoanSetupTier>(catalog.tier as LoanSetupTier);
  const [tierReason, setTierReason] = useState("");
  const [ownerRoles, setOwnerRoles] = useState<string[]>(
    splitOwnerRoleCodes(catalog.ownerRoleCode),
  );
  const [section, setSection] = useState(catalog.section ?? LOAN_SETUP_SECTIONS[0]);
  const [dueDateFormula, setDueDateFormula] = useState(catalog.dueDateFormula ?? "");
  const [description, setDescription] = useState(catalog.description ?? "");

  // Re-seed on open so a previously-edited item never bleeds into another.
  useEffect(() => {
    if (!open) return;
    setTitle(catalog.title);
    setTier(catalog.tier as LoanSetupTier);
    setTierReason("");
    setOwnerRoles(splitOwnerRoleCodes(catalog.ownerRoleCode));
    setSection(catalog.section ?? LOAN_SETUP_SECTIONS[0]);
    setDueDateFormula(catalog.dueDateFormula ?? "");
    setDescription(catalog.description ?? "");
  }, [open, catalog]);

  const tierChanged = tier !== catalog.tier;
  const titleValid = title.trim().length > 0 && title.trim().length <= TITLE_MAX;
  const ownerValid = ownerRoles.length > 0;
  const formulaValid = dueDateFormula.trim().length > 0;
  const tierValid = !tierChanged || tierReason.trim().length > 0;
  const formValid = titleValid && ownerValid && formulaValid && tierValid;

  const onSave = async () => {
    try {
      await update.mutateAsync({
        id: catalog.id,
        body: {
          title: title.trim(),
          ownerRoleCode: joinOwnerRoleCodes(ownerRoles),
          section,
          dueDateFormula: dueDateFormula.trim(),
          description: description.trim() === "" ? null : description.trim(),
          tier: tierChanged ? tier : null,
          tierChangeReason: tierChanged ? tierReason.trim() : null,
        },
      });
      toast.success(`${catalog.code} updated. Changes apply to all loans.`);
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Edit ${catalog.code}`}
      description="Edits the catalog template — changes show on every loan's Setup checklist."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSave()}
            disabled={!formValid || update.isPending}
            data-testid="setup-catalog-edit-save"
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4" data-testid="setup-catalog-edit-modal">
        <div className="flex items-center gap-space-2">
          <Badge status="draft">{catalog.code}</Badge>
          <span className="text-caption text-gray-mid">Code is permanent.</span>
        </div>

        <FormField label="Title" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={TITLE_MAX}
            data-testid="setup-catalog-edit-title"
          />
        </FormField>

        <TierField value={tier} onChange={setTier} />
        {tierChanged && (
          <div
            className="rounded-md border border-status-warning bg-white-warm p-space-2 flex flex-col gap-space-2"
            data-testid="setup-tier-change-warning"
          >
            <p className="text-small text-onyx m-0">
              Changing the tier to{" "}
              <span className="font-medium">{loanSetupTierLabel(tier)}</span>{" "}
              changes the completion gate on every loan's checklist immediately.
            </p>
            <FormField label="Justification for tier change" required>
              <Textarea
                value={tierReason}
                onChange={(e) => setTierReason(e.target.value)}
                rows={2}
                placeholder="Required — recorded in the audit log."
                data-testid="setup-tier-change-reason"
              />
            </FormField>
          </div>
        )}

        <FormField label="Section" required>
          <select
            className="px-space-2 py-space-1 border border-border-light rounded-md text-small bg-white-pure"
            value={section}
            onChange={(e) => setSection(e.target.value)}
          >
            {LOAN_SETUP_SECTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </FormField>

        <OwnerRolesField value={ownerRoles} onChange={setOwnerRoles} />

        <DueDateFormulaField
          value={dueDateFormula}
          onChange={setDueDateFormula}
          firstPaymentDate={loan.firstPaymentDate}
        />

        <FormField label="Description">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </FormField>
      </div>
    </Modal>
  );
}
